import type { ReactNode } from "react";

export type HeroCardVariant = "default" | "compact";

export interface HeroCardProps {
  title: ReactNode;
  subtitle?: ReactNode;
  statusSlot?: ReactNode;
  actionSlot?: ReactNode;
  variant?: HeroCardVariant;
  className?: string;
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

/**
 * HeroCard — high-emphasis page-header surface used on Dashboard, Orders,
 * Products, and (via PageHeader.variant="hero") any other seller index page.
 *
 * Visual spec: teal surface (var(--color-hero-surface) = --primary-700),
 * white ink (var(--color-hero-ink)), subtle ink for subtitles
 * (var(--color-hero-ink-subtle)), radius var(--radius-lg) (12px), padding
 * via --spacing-hero-pad-default on mobile / --spacing-hero-pad-desktop on
 * md+.
 *
 * RTL: this component uses CSS logical properties only. Title text uses
 * text-start. The flex row uses justify-between so the actionSlot pins to
 * the inline-end side automatically under RTL. Physical-direction
 * utilities are not used.
 */
export function HeroCard({
  title,
  subtitle,
  statusSlot,
  actionSlot,
  variant = "default",
  className,
}: HeroCardProps) {
  const paddingClass =
    variant === "compact"
      ? "p-[var(--spacing-hero-pad-compact)]"
      : "p-[var(--spacing-hero-pad-default)] md:p-[var(--spacing-hero-pad-desktop)]";

  return (
    <section
      className={cx(
        "rounded-[var(--radius-lg)] bg-[color:var(--color-hero-surface)] text-[color:var(--color-hero-ink)]",
        paddingClass,
        className,
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-start text-2xl font-semibold text-[color:var(--color-hero-ink)] sm:text-3xl">
              {title}
            </h1>
            {statusSlot ? <span className="shrink-0">{statusSlot}</span> : null}
          </div>
          {subtitle ? (
            <p className="text-start text-sm text-[color:var(--color-hero-ink-subtle)]">
              {subtitle}
            </p>
          ) : null}
        </div>
        {actionSlot ? (
          <div className="shrink-0 self-start">{actionSlot}</div>
        ) : null}
      </div>
    </section>
  );
}
